/**
 * #933 PR 2b — Settings → Capacity: daily limits by service.
 *
 * Ted's A6 numbers (daycare 40, boarding 18, grooming 5) reached HTHD prod
 * through `seed-hthd-catalog.ts`. This card is the owner's write path onto
 * the SAME rows: one service-wide rule per service (`dayOfWeek: null`, the
 * dashboard meter's denominator and `createBooking`'s cap) and, behind an
 * expander, one rule per weekday for the shops that take fewer dogs on a
 * Saturday. Time-slot rules (grooming, assessments) are counted here and
 * edited where they belong — the assessment schedule card owns its own.
 *
 * Design Philosophy principles served:
 *  - #1 One glance, one truth: each service shows the number the engine
 *    reads, once; a weekday cell without its own rule shows the every-day
 *    number muted so the eye reads the effective limit, not a blank.
 *  - #2 The next action is obvious: one control per number (Set / Change),
 *    a two-tap delete (the PricingRules pattern — no browser confirm), and a
 *    non-owner sees the numbers with a plain reason, never a dead control.
 *  - #5 Never lie about data: "Not set" carries a "Platform default" mark —
 *    the fallback is named, never rendered as a zero. A duplicate answer
 *    from the server (another desk or a script wrote the rule since this
 *    page loaded) reloads the row so the existing rule is the one on screen.
 *  - Five states: loading · error(+retry) · empty (no rules — teaching copy)
 *    · ready · saving (per-row in-flight lock). Stale does not apply: the
 *    card re-reads after every write and shows nothing it did not fetch.
 *
 * Counter Test: Ted sets daycare to 40 and boarding to 18 at the desk; the
 * dashboard meters show those numbers with the rules source, and the next
 * online booking is refused at 41. No script, no session.
 *
 * Server contract: GET/POST /api/v2/admin/tenant/capacity-rules,
 * PATCH/DELETE /capacity-rules/:id. Read business-config:read:all; write
 * business-config:write:all (owner, manager). Create is find-then-409 on the rule's
 * natural key (#670).
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Badge, Button, Card, EmptyState, Input, Skeleton } from '@ferro/ui';
import { capacityPoolParent } from '@ferro/core/modules/booking/capacity-groups.js';
import {
  adminTenantCapacityApi,
  type TenantCapacityRule,
  type TenantCapacityService,
  type TenantCapacityView,
} from '../../lib/api.js';
import { WEEKDAYS_MON_FIRST, WEEKDAY_LONG, WEEKDAY_SHORT } from './capacity-helpers.js';

interface CapacityRulesCardProps {
  apiUrl: string;
  /** Holds business-config:write:all (owner, manager). Everyone else sees read-only. */
  canEdit: boolean;
}

type Status = 'loading' | 'error' | 'ready';

/** The service whose rules ARE the assessment schedule — edited in that card, never here. */
const ASSESSMENT_SERVICE_NAME = 'assessment';
/** Mirrors the boundary schema's MAX_CAPACITY_VALUE — a bigger number is a typo, not a policy. */
export const MAX_CAPACITY = 5000;

export interface ServiceCapacityRow {
  service: TenantCapacityService;
  /** `dayOfWeek: null` + `startTime: null` — the service-wide default. */
  everyDay: TenantCapacityRule | null;
  /** Index 0 = Sunday … 6 = Saturday → that weekday's whole-day rule. */
  byWeekday: ReadonlyArray<TenantCapacityRule | null>;
  /** Rules with a `startTime` — offered slots, counted here, edited elsewhere. */
  slotRuleCount: number;
  /** Set when this service counts against another service's limit (half-day → daycare). */
  pooledInto: TenantCapacityService | null;
}

/** The table's rows from the GET — pure, so the tests can pin the shape. */
export function buildServiceRows(view: TenantCapacityView): ServiceCapacityRow[] {
  const byName = new Map(view.services.map((s) => [s.name, s]));
  return view.services
    .filter((s) => s.isActive && s.name !== ASSESSMENT_SERVICE_NAME)
    .sort((a, b) => a.sortOrder - b.sortOrder || a.displayName.localeCompare(b.displayName))
    .map((service) => {
      const own = view.rules.filter((r) => r.serviceTypeId === service.id && r.resourceTypeId === null);
      const dayRules = own.filter((r) => r.startTime === null);
      const byWeekday: Array<TenantCapacityRule | null> = [null, null, null, null, null, null, null];
      for (const r of dayRules) if (r.dayOfWeek !== null) byWeekday[r.dayOfWeek] = r;
      const parentName = capacityPoolParent(service.name);
      const pooledInto = parentName === service.name ? null : (byName.get(parentName) ?? null);
      return {
        service,
        everyDay: dayRules.find((r) => r.dayOfWeek === null) ?? null,
        byWeekday,
        slotRuleCount: own.length - dayRules.length,
        pooledInto,
      };
    });
}

export function validateCapacity(raw: string): { ok: true; value: number } | { ok: false; message: string } {
  const n = Number(raw);
  if (raw.trim() === '' || !Number.isInteger(n) || n < 0 || n > MAX_CAPACITY) {
    return { ok: false, message: `Enter a whole number between 0 and ${MAX_CAPACITY}.` };
  }
  return { ok: true, value: n };
}

const keyOf = (serviceId: string, day: number | null): string => `${serviceId}|${day === null ? 'all' : day}`;

export function CapacityRulesCard({ apiUrl, canEdit }: CapacityRulesCardProps): JSX.Element {
  const [status, setStatus] = useState<Status>('loading');
  const [loadError, setLoadError] = useState<string | null>(null);
  const [view, setView] = useState<TenantCapacityView | null>(null);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [editing, setEditing] = useState<{ key: string; value: string } | null>(null);
  const editInputRef = useRef<HTMLInputElement>(null);
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [rowError, setRowError] = useState<{ key: string; message: string } | null>(null);
  const [confirmDeleteKey, setConfirmDeleteKey] = useState<string | null>(null);
  const [saved, setSaved] = useState<string | null>(null);

  /** `initial` flips the card to the skeleton; `refresh` re-reads behind the table after a write. */
  const load = useCallback(
    async (mode: 'initial' | 'refresh' = 'initial'): Promise<boolean> => {
      if (mode === 'initial') {
        setStatus('loading');
        setLoadError(null);
        setSaved(null);
      }
      const res = await adminTenantCapacityApi.listRules(apiUrl);
      if (res.error || !res.data) {
        if (mode === 'initial') {
          setLoadError(res.error ?? 'Could not load capacity limits.');
          setStatus('error');
        }
        return false;
      }
      setView(res.data);
      setStatus('ready');
      return true;
    },
    [apiUrl],
  );

  useEffect(() => {
    void load();
  }, [load]);

  const rows = useMemo(() => (view ? buildServiceRows(view) : []), [view]);
  const anyRule = rows.some((r) => r.everyDay !== null || r.byWeekday.some((d) => d !== null));

  // One editor is open at a time; focus it when it opens or moves to another
  // limit — keyed on the key, not the value, so typing never re-runs this.
  const editingKey = editing?.key ?? null;
  useEffect(() => {
    if (editingKey !== null) editInputRef.current?.focus();
  }, [editingKey]);

  const startEdit = useCallback((key: string, current: TenantCapacityRule | null) => {
    setEditing({ key, value: current ? String(current.maxCapacity) : '' });
    setRowError(null);
    setSaved(null);
    setConfirmDeleteKey(null);
  }, []);

  const commit = useCallback(
    async (row: ServiceCapacityRow, day: number | null, existing: TenantCapacityRule | null) => {
      if (!editing) return;
      const key = keyOf(row.service.id, day);
      const parsed = validateCapacity(editing.value);
      if (!parsed.ok) {
        setRowError({ key, message: parsed.message });
        return;
      }
      setBusyKey(key);
      setRowError(null);
      setSaved(null);
      const res = existing
        ? await adminTenantCapacityApi.updateRule(apiUrl, existing.id, { maxCapacity: parsed.value })
        : await adminTenantCapacityApi.createRule(apiUrl, {
            serviceTypeId: row.service.id,
            dayOfWeek: day,
            startTime: null,
            maxCapacity: parsed.value,
          });
      if (res.error || !res.data) {
        // A 409 means a rule for this key already exists (written from
        // another desk or a script since this page loaded). Re-read so the
        // existing rule is the one on screen; the editor stays open on it,
        // so the owner's next Save changes that rule instead of duplicating.
        const reloaded = !existing ? await load('refresh') : false;
        setBusyKey(null);
        setRowError({
          key,
          message: `${res.error ?? 'Could not save that limit.'}${reloaded ? ' The current rule is shown here — save again to change it.' : ''}`,
        });
        return;
      }
      await load('refresh');
      setBusyKey(null);
      setEditing(null);
      setSaved(
        `${row.service.displayName}: ${day === null ? 'every day' : WEEKDAY_LONG[day]} is now ${parsed.value}. The next booking honors it.`,
      );
    },
    [apiUrl, editing, load],
  );

  const remove = useCallback(
    async (row: ServiceCapacityRow, day: number | null, rule: TenantCapacityRule) => {
      const key = keyOf(row.service.id, day);
      setBusyKey(key);
      setRowError(null);
      setSaved(null);
      const res = await adminTenantCapacityApi.deleteRule(apiUrl, rule.id);
      setConfirmDeleteKey(null);
      if (res.error) {
        setBusyKey(null);
        setRowError({ key, message: res.error });
        return;
      }
      await load('refresh');
      setBusyKey(null);
      setSaved(
        day === null
          ? `${row.service.displayName}: limit removed. It uses the platform default until you set one.`
          : `${row.service.displayName}: ${WEEKDAY_LONG[day]} now uses the every-day limit.`,
      );
    },
    [apiUrl, load],
  );

  // ── Loading (skeleton, never a spinner wall) ────────────────────────
  if (status === 'loading') {
    return (
      <Card title="Daily limits by service">
        <div data-testid="capacity-rules-loading">
          <Skeleton variant="text" lines={4} />
        </div>
      </Card>
    );
  }

  // ── Error (honest, with recovery) ───────────────────────────────────
  if (status === 'error' || !view) {
    return (
      <Card title="Daily limits by service">
        <div data-testid="capacity-rules-error">
          <Alert variant="error">{loadError ?? 'Could not load capacity limits.'}</Alert>
          <div className="ferro-settings__actions">
            <Button variant="outline" onClick={() => void load()}>
              Try again
            </Button>
          </div>
        </div>
      </Card>
    );
  }

  const renderEditor = (row: ServiceCapacityRow, day: number | null, rule: TenantCapacityRule | null, testId: string) => {
    const key = keyOf(row.service.id, day);
    const busy = busyKey === key;
    return (
      <div className="ferro-capacity__inline">
        <Input
          type="number"
          inputMode="numeric"
          min={0}
          max={MAX_CAPACITY}
          step={1}
          value={editing?.value ?? ''}
          onChange={(e) => setEditing({ key, value: e.target.value })}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              void commit(row, day, rule);
            } else if (e.key === 'Escape') {
              setEditing(null);
            }
          }}
          aria-label={`${row.service.displayName} limit, ${day === null ? 'every day' : WEEKDAY_LONG[day]}`}
          ref={editInputRef}
          data-testid={`${testId}-input`}
        />
        <Button size="sm" variant="primary" isLoading={busy} onClick={() => void commit(row, day, rule)} data-testid={`${testId}-save`}>
          Save
        </Button>
        <Button size="sm" variant="ghost" disabled={busy} onClick={() => setEditing(null)} data-testid={`${testId}-cancel`}>
          Cancel
        </Button>
      </div>
    );
  };

  const renderActions = (row: ServiceCapacityRow, day: number | null, rule: TenantCapacityRule | null, testId: string) => {
    if (!canEdit) return null;
    const key = keyOf(row.service.id, day);
    const busy = busyKey === key;
    return (
      <div className="ferro-capacity__actions">
        <Button size="sm" variant="ghost" disabled={busy} onClick={() => startEdit(key, rule)} data-testid={`${testId}-edit`}>
          {rule ? 'Change' : 'Set'}
        </Button>
        {rule ? (
          confirmDeleteKey === key ? (
            <>
              <Button size="sm" variant="danger" isLoading={busy} onClick={() => void remove(row, day, rule)} data-testid={`${testId}-delete-confirm`}>
                Confirm remove
              </Button>
              <Button size="sm" variant="ghost" disabled={busy} onClick={() => setConfirmDeleteKey(null)}>
                Keep
              </Button>
            </>
          ) : (
            <Button size="sm" variant="ghost" disabled={busy} onClick={() => { setConfirmDeleteKey(key); setEditing(null); }} data-testid={`${testId}-delete`}>
              Remove
            </Button>
          )
        ) : null}
      </div>
    );
  };

  return (
    <Card
      title="Daily limits by service"
      subtitle="How many dogs each service takes per day. The dashboard meters and every online booking read these numbers."
    >
      <div className="ferro-settings__card" data-testid="capacity-rules-ready">
        {rows.length === 0 ? (
          <div data-testid="capacity-rules-no-services">
            <EmptyState
              title="No services yet"
              description="Services come from your catalog. Once one exists it appears here with its daily limit."
            />
          </div>
        ) : (
          <>
            {!anyRule ? (
              <p className="ferro-settings__teaching" data-testid="capacity-rules-empty">
                No limits set yet. Every service uses the platform default until you set a number here —
                start with the one that fills up first.
              </p>
            ) : null}
            {saved ? (
              <div data-testid="capacity-rules-saved">
                <Alert variant="success">{saved}</Alert>
              </div>
            ) : null}
            <ul className="ferro-capacity__list">
              {rows.map((row) => {
                const sid = row.service.id;
                const allKey = keyOf(sid, null);
                const weekdayCount = row.byWeekday.filter((d) => d !== null).length;
                const isOpen = expanded[sid] === true;
                const rowErr = rowError && rowError.key.startsWith(`${sid}|`) ? rowError : null;
                return (
                  <li key={sid} className="ferro-capacity__row" data-testid={`capacity-rule-row-${sid}`}>
                    <div className="ferro-capacity__name">
                      {row.service.displayName}
                      {row.slotRuleCount > 0 ? (
                        <span className="ferro-muted ferro-capacity__meta">
                          {' '}
                          · {row.slotRuleCount} time-slot limit{row.slotRuleCount === 1 ? '' : 's'} from your catalog
                        </span>
                      ) : null}
                    </div>

                    {row.pooledInto ? (
                      <div className="ferro-capacity__pooled" data-testid={`capacity-pooled-${sid}`}>
                        <span className="ferro-muted">Counts toward {row.pooledInto.displayName}</span>
                      </div>
                    ) : editing?.key === allKey ? (
                      renderEditor(row, null, row.everyDay, `capacity-${sid}`)
                    ) : (
                      <div className="ferro-capacity__value">
                        <span
                          className={`ferro-capacity__cap${row.everyDay ? '' : ' ferro-capacity__cap--unset'}`}
                          data-testid={`capacity-cap-${sid}`}
                        >
                          {row.everyDay ? row.everyDay.maxCapacity : 'Not set'}
                        </span>
                        {row.everyDay ? (
                          <span className="ferro-muted">per day</span>
                        ) : (
                          <span data-testid={`capacity-default-${sid}`}>
                            <Badge variant="platform">Platform default</Badge>
                          </span>
                        )}
                      </div>
                    )}

                    {row.pooledInto ? null : (
                      <div className="ferro-capacity__row-actions">
                        {editing?.key === allKey ? null : renderActions(row, null, row.everyDay, `capacity-${sid}`)}
                        <Button
                          size="sm"
                          variant="ghost"
                          aria-expanded={isOpen}
                          aria-controls={`capacity-week-${sid}`}
                          onClick={() => setExpanded((e) => ({ ...e, [sid]: !isOpen }))}
                          data-testid={`capacity-expand-${sid}`}
                        >
                          {isOpen ? 'Hide weekdays' : weekdayCount > 0 ? `By weekday (${weekdayCount} set)` : 'By weekday'}
                        </Button>
                      </div>
                    )}

                    {rowErr ? (
                      <div className="ferro-capacity__row-error" data-testid={`capacity-row-error-${sid}`}>
                        <Alert variant="error">{rowErr.message}</Alert>
                      </div>
                    ) : null}

                    {isOpen && !row.pooledInto ? (
                      <div id={`capacity-week-${sid}`} className="ferro-capacity__week" data-testid={`capacity-week-${sid}`}>
                        {WEEKDAYS_MON_FIRST.map((d) => {
                          const rule = row.byWeekday[d] ?? null;
                          const key = keyOf(sid, d);
                          const testId = `capacity-day-${sid}-${d}`;
                          const effective = rule ?? row.everyDay;
                          return (
                            <div key={d} className="ferro-capacity__day" data-testid={testId}>
                              <span className="ferro-capacity__day-label">{WEEKDAY_SHORT[d]}</span>
                              {editing?.key === key ? (
                                renderEditor(row, d, rule, testId)
                              ) : (
                                <>
                                  <span
                                    className={`ferro-capacity__day-cap${rule ? '' : ' ferro-capacity__day-cap--inherit'}`}
                                    title={rule ? `${WEEKDAY_LONG[d]} has its own limit` : 'Same as every day'}
                                    data-testid={`${testId}-cap`}
                                  >
                                    {effective ? effective.maxCapacity : '—'}
                                  </span>
                                  {renderActions(row, d, rule, testId)}
                                </>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    ) : null}
                  </li>
                );
              })}
            </ul>
            {!canEdit ? (
              <p className="ferro-muted" data-testid="capacity-rules-readonly-note">
                Only an owner or manager can change these limits.
              </p>
            ) : null}
            <p className="ferro-muted ferro-settings__footnote">
              Assessment days and times are set in the Assessment schedule card below.
            </p>
          </>
        )}
      </div>
    </Card>
  );
}
